import { Ban, CheckCircle, Loader2, Search, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/AlertDialog.jsx";
import { Button } from "../../components/ui/Button.jsx";
import { Card, CardContent } from "../../components/ui/Card.jsx";
import { Input } from "../../components/ui/Input.jsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/Select.jsx";
import { useToast } from "../../hooks/useToast.js";
import {
  activateUser,
  fetchUsers,
  suspendUser,
  terminateUser,
} from "../../services/superAdminService.js";

export default function UserManagement() {
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});

  // Confirm dialog state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  const loadUsers = async (p = page) => {
    setLoading(true);
    try {
      const res = await fetchUsers({
        page: p,
        limit: 20,
        search,
        role: roleFilter,
        status: statusFilter,
      });
      setUsers(res.data || []);
      setPagination(res.pagination || {});
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers(1);
    setPage(1);
  }, [search, roleFilter, statusFilter]);

  const openConfirm = (action, userId, userName) => {
    setConfirmAction({ action, userId, userName });
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    if (!confirmAction) return;
    const { action, userId, userName } = confirmAction;
    setConfirmOpen(false);
    setConfirmAction(null);

    try {
      if (action === "suspend") await suspendUser(userId);
      else if (action === "activate") await activateUser(userId);
      else if (action === "terminate") await terminateUser(userId);
      toast({ title: `User "${userName}" ${action}d successfully` });
      loadUsers(page);
    } catch (err) {
      toast({ variant: "destructive", title: "Action failed", description: err.message });
    }
  };

  const getConfirmMessage = () => {
    if (!confirmAction) return {};
    const { action, userName } = confirmAction;
    if (action === "terminate")
      return {
        title: `Terminate "${userName}"?`,
        desc: "This will permanently soft-delete this user. They will lose access to the platform.",
      };
    if (action === "suspend")
      return {
        title: `Suspend "${userName}"?`,
        desc: "This user will be blocked from logging in until reactivated.",
      };
    return {
      title: `Activate "${userName}"?`,
      desc: "This will restore the user's access to the platform.",
    };
  };

  const confirmMsg = getConfirmMessage();
  const formatDate = (d) => (d ? new Date(d).toLocaleDateString() : "Never");
  const formatDateTime = (d) => (d ? new Date(d).toLocaleString() : "Never");

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-bold text-gray-900">User Management</h1>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email..."
            className="h-8 text-xs pl-9"
          />
        </div>
        <Select value={roleFilter} onValueChange={(v) => setRoleFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="h-8 text-xs w-40">
            <SelectValue placeholder="All Roles" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="business_admin">Business Admin</SelectItem>
            <SelectItem value="sales_person">Sales Person</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
            <SelectItem value="technician">Technician</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="h-8 text-xs w-36">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card className="border-gray-100 overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-left p-3 font-medium text-gray-500">Name</th>
                    <th className="text-left p-3 font-medium text-gray-500">Email</th>
                    <th className="text-left p-3 font-medium text-gray-500">Role</th>
                    <th className="text-left p-3 font-medium text-gray-500">Company</th>
                    <th className="text-left p-3 font-medium text-gray-500">Leads</th>
                    <th className="text-left p-3 font-medium text-gray-500">Status</th>
                    <th className="text-left p-3 font-medium text-gray-500">Last Login</th>
                    <th className="text-left p-3 font-medium text-gray-500">Joined</th>
                    <th className="text-left p-3 font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                      <td className="p-3 font-medium text-gray-800">{user.name}</td>
                      <td className="p-3 text-gray-600">{user.email}</td>
                      <td className="p-3">
                        <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[10px]">
                          {user.user_role}
                        </span>
                      </td>
                      <td className="p-3 text-gray-600">{user.company_name || "-"}</td>
                      <td className="p-3 text-gray-700 font-medium">{user.leads_count}</td>
                      <td className="p-3">
                        {user.is_suspended ? (
                          <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-600 text-[10px] font-medium">
                            Suspended
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full bg-green-50 text-green-600 text-[10px] font-medium">
                            Active
                          </span>
                        )}
                      </td>
                      <td className="p-3 text-gray-500">{formatDateTime(user.last_login_at)}</td>
                      <td className="p-3 text-gray-500">{formatDate(user.created_at)}</td>
                      <td className="p-3">
                        <div className="flex gap-1">
                          {user.is_suspended ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              title="Activate"
                              onClick={() => openConfirm("activate", user.id, user.name)}
                            >
                              <CheckCircle size={13} className="text-green-500" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              title="Suspend"
                              onClick={() => openConfirm("suspend", user.id, user.name)}
                            >
                              <Ban size={13} className="text-orange-500" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            title="Terminate"
                            onClick={() => openConfirm("terminate", user.id, user.name)}
                          >
                            <Trash2 size={13} className="text-red-400" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr>
                      <td colSpan={9} className="p-6 text-center text-gray-400">
                        No users found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-500">
            Page {pagination.page} of {pagination.pages} ({pagination.total} users)
          </p>
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              disabled={page <= 1}
              onClick={() => { setPage(page - 1); loadUsers(page - 1); }}
            >
              Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              disabled={page >= pagination.pages}
              onClick={() => { setPage(page + 1); loadUsers(page + 1); }}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmMsg.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirmMsg.desc}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm}>Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
